// Shared helpers for membership status, day pass expiry, and mini account IDs.
// Used by both the public AdultMembership flow and member-only feature gates.

import {
  collection, getDocs, query as fsQuery, where as fsWhere,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Convert an email to the deterministic doc id used for mini accounts.
 * Mirrors the `pending_{emailkey}` pattern used for coach invitations.
 */
export function emailToMiniId(email) {
  if (!email) return null;
  const key = String(email).toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
  return `mini_${key}`;
}

/**
 * True if the user currently has an active membership benefit.
 * Single source of truth for "is this user a member right now" checks.
 *
 *  - Subscription: active so long as currentPeriodEnd is in the future
 *      (cancel_at_period_end still grants access until the end of the period).
 *  - One-time / day pass: active so long as expiresAt is in the future.
 *  - Frozen: never active. A freeze pauses access outright, so the paid-through
 *      date is irrelevant.
 */
export function isActiveMember(userData, now = new Date()) {
  const m = userData && userData.membership;
  if (!m) return false;

  // A freeze pauses access regardless of how far the period runs.
  if (m.status === 'frozen') return false;

  if (m.pricingMode === 'recurring' || (isMemberInGoodStanding(userData) && m.stripeSubscriptionId)) {
    // No period end recorded — fall back to good standing, so prepaid annual
    // members (status 'trialing') aren't treated as lapsed.
    if (!m.currentPeriodEnd) return isMemberInGoodStanding(userData);
    const end = m.currentPeriodEnd.toDate ? m.currentPeriodEnd.toDate() : new Date(m.currentPeriodEnd);
    return end > now;
  }

  if (m.pricingMode === 'one_time' || m.pricingMode === 'one-time') {
    if (!m.expiresAt) return false;
    const exp = m.expiresAt.toDate ? m.expiresAt.toDate() : new Date(m.expiresAt);
    return exp > now;
  }

  return false;
}

/**
 * True if the user is a member in good standing — the club-wide definition:
 * 'active' (paying) or 'trialing' (prepaid, full access).
 *
 * Use this for anything that should treat a trialing member as a real member:
 * tier badges, member-facing labels, "is this person a member" checks.
 *
 * Deliberately excludes 'frozen' — a frozen membership is paused, granting no
 * booking and no gate access — as well as 'past_due' and 'canceled'.
 */
/**
 * The membership's EFFECTIVE status, accounting for expiry.
 *
 * membership.status is written at purchase and never rewritten — nothing sweeps
 * it when a pass lapses. A day pass bought last week therefore still reads
 * 'active' in Firestore long after it expired. This derives the real status at
 * read time, so no scheduled job is needed and stored state can't drift:
 *
 *   one-time / day pass, past expiresAt   -> 'expired'
 *   one-time / day pass, no expiresAt     -> 'expired' (malformed, not eternal)
 *   anything else                         -> the stored status
 *
 * Recurring memberships are untouched: their lifecycle is driven by Stripe
 * webhooks writing status, and currentPeriodEnd is handled by isActiveMember.
 */
export function membershipStatus(userData, now = new Date()) {
  const m = userData && userData.membership;
  if (!m) return null;
  const oneTime = m.pricingMode === 'one_time' || m.pricingMode === 'one-time' || m.isDayPass === true;
  if (!oneTime) return m.status || null;
  if (!m.expiresAt) return 'expired';
  const exp = m.expiresAt.toDate ? m.expiresAt.toDate() : new Date(m.expiresAt);
  return exp > now ? (m.status || null) : 'expired';
}

export function isMemberInGoodStanding(userData) {
  const status = membershipStatus(userData);
  return status === 'active' || status === 'trialing';
}

/**
 * Whether this account reaches a junior with sessions left — the second way to
 * earn the member rate, alongside an adult membership.
 *
 * DISPLAY ONLY. registerForLeague runs the same three lookups server-side and
 * decides the actual charge; this exists so the price on screen matches the one
 * about to be taken, not to grant anything.
 *
 * Three links because a junior's sessions can be reached three ways: their own
 * account, the legacy parentUserId, and the parentUserIds array that replaced
 * it. A parent registering their child appears only in the parent fields.
 *
 * "Active" is not athlete.status — that came in with the PushPress import and
 * most juniors holding sessions have none. It is the club's own definition,
 * shared with check-in and the schedule: not archived, not merged away.
 * A comped athlete counts: their balance is left at 0 by design while they
 * attend free.
 */
export async function hasJuniorWithSessions(uid) {
  if (!uid) return null;
  try {
    const athletes = collection(db, 'athletes');
    const snaps = await Promise.all([
      getDocs(fsQuery(athletes, fsWhere('userId', '==', uid))),
      getDocs(fsQuery(athletes, fsWhere('parentUserId', '==', uid))),
      getDocs(fsQuery(athletes, fsWhere('parentUserIds', 'array-contains', uid))),
    ]);
    const seen = new Set();
    for (const snap of snaps) {
      for (const d of snap.docs) {
        if (seen.has(d.id)) continue;
        seen.add(d.id);
        const a = d.data();
        if (a.isArchived === true || a.mergedInto) continue;
        if (a.compedSessions === true || (Number(a.sessionBalance) || 0) > 0) {
          return {
            athleteId: d.id,
            name: `${String(a.firstName || '').trim()} ${String(a.lastName || '').trim()}`.trim(),
            sessionBalance: Number(a.sessionBalance) || 0,
            comped: a.compedSessions === true,
          };
        }
      }
    }
    return null;
  } catch {
    // Denied or offline: fall back to showing the list price. The server still
    // applies the discount, so the worst case is a pleasant surprise at
    // checkout rather than a charge nobody expected.
    return null;
  }
}

/**
 * True if the person should visually read as a member of their tier — i.e.
 * whether to render the GOLD/SILVER/etc. badge in place of the generic
 * 'member' role chip.
 *
 * Broader than good standing on purpose: 'past_due' members are still members
 * whose payment is retrying, so they keep the badge. 'frozen' does not, since a
 * paused membership grants nothing; neither does 'canceled'.
 *
 * This is a DISPLAY predicate — never use it to grant booking or gate access.
 * Use isMemberInGoodStanding() for that.
 */
export function showsMemberTier(userData) {
  const status = membershipStatus(userData);
  return isMemberInGoodStanding(userData) || status === 'past_due';
}

/**
 * True if this account is a court renter rather than a club member.
 *
 * A renter's tier lives in the same `membership` map as a membership, with the
 * same status 'active', so every status-based check reads them as a member.
 * The pricing mode is the only thing that separates the two.
 *
 * A renter never reads as a member no matter what their tier is called: a
 * rental tier named "Gold Rental" is not a Gold membership. Someone who holds a
 * real Silver or Gold membership is not a renter by this test — their tier's
 * pricing mode is recurring or one_time — so they keep their badge.
 */
export function isCourtRenter(userData) {
  return userData?.membership?.pricingMode === 'court_rental';
}

/**
 * True if the user has an active day pass right now.
 * Useful for the "Already active today" button label on AdultMembership.
 */
export function hasActiveDayPass(userData, now = new Date()) {
  const m = userData && userData.membership;
  if (!m) return false;
  if (!m.isDayPass) return false;
  if (!m.expiresAt) return false;
  const exp = m.expiresAt.toDate ? m.expiresAt.toDate() : new Date(m.expiresAt);
  return exp > now;
}

/**
 * Default closing hour if no rentalTimeBlocks are configured.
 * 22 = 10:00 PM. Used as a safe fallback only.
 */
export const DEFAULT_CLOSE_HOUR = 22;

/**
 * Calculate when a day pass purchased at `now` expires.
 * Reads today's `rentalTimeBlocks`, takes the latest endTime, and uses that
 * as the expiry. If no blocks exist for today, falls back to DEFAULT_CLOSE_HOUR.
 *
 * If `now` is already past today's close, the pass extends to the next day's
 * close (people who buy at 11pm get tomorrow's session, not 0 hours of access).
 */
export async function calculateDayPassExpiry(now = new Date()) {
  try {
    const snap = await getDocs(collection(db, 'rentalTimeBlocks'));
    const blocks = snap.docs.map(d => d.data()).filter(b => !!b);

    const SHORT_DAY = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const openHours = blocks.find(b => (b.name || '').toLowerCase() === 'open hours');
    const candidateBlocks = openHours ? [openHours] : blocks;

    let target = new Date(now);
    for (let attempt = 0; attempt < 2; attempt++) {
      const dayShort = SHORT_DAY[target.getDay()];
      const dayLong = target.toLocaleDateString('en-US', { weekday: 'long' });
      const todayBlocks = candidateBlocks.filter(b => {
        const list = Array.isArray(b.days)
          ? b.days
          : Array.isArray(b.daysOfWeek) ? b.daysOfWeek : [];
        const normalized = list.map(s => String(s).toLowerCase());
        return normalized.includes(dayShort.toLowerCase()) ||
               normalized.includes(dayLong.toLowerCase());
      });
      if (todayBlocks.length > 0) {
        const latest = todayBlocks.reduce((max, b) => {
          if (!b.endTime || typeof b.endTime !== 'string') return max;
          return !max || b.endTime > max ? b.endTime : max;
        }, null);
        if (latest) {
          const [h, m] = latest.split(':').map(Number);
          const expiry = new Date(target);
          expiry.setHours(h, m || 0, 0, 0);
          if (expiry > now) return expiry;
        }
      }
      target.setDate(target.getDate() + 1);
    }

    const fallback = new Date(now);
    fallback.setHours(DEFAULT_CLOSE_HOUR, 0, 0, 0);
    if (fallback <= now) fallback.setDate(fallback.getDate() + 1);
    return fallback;
  } catch (e) {
    console.error('calculateDayPassExpiry error, using fallback:', e);
    const fallback = new Date(now);
    fallback.setHours(DEFAULT_CLOSE_HOUR, 0, 0, 0);
    if (fallback <= now) fallback.setDate(fallback.getDate() + 1);
    return fallback;
  }
}

/**
 * Format closing time as a friendly string ("10:00 PM").
 * Used on the AdultMembership day pass card.
 */
export function formatCloseTime(date) {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
